import {
  Box,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Link,
  Center,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import numeral from 'numeral'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { Link as reactLink } from 'react-router-dom'
import { colors } from '../../../ui-kit'
import { currencySymbol } from '../../../utils'
import type {
  LineItem,
  UpcomingInvoiceSuccessResponse,
  UpcomingInvoiceErrorResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'

const UpcomingInvoice = (): ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [data, setData] = useState<UpcomingInvoiceSuccessResponse>()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  const { response, request } = useFetch<UpcomingInvoiceSuccessResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const res = (await request.get(
        `/api/clients/${user.clientNumber}/invoice/upcoming`,
      )) as UpcomingInvoiceErrorResponse | UpcomingInvoiceSuccessResponse

      if (request.error) {
        if (response.status === 404) {
          setError((res as UpcomingInvoiceErrorResponse).message ?? '')
        } else {
          setError(
            t(
              'error_fetching_invoice_data',
              'Error in fetching upcoming invoice details. Try refreshing the page.',
            ),
          )
        }
        setLoading(false)
      } else {
        setData(res as UpcomingInvoiceSuccessResponse)
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo<LineItem[] | undefined>(() => {
    if (data?.lineItems && data.lineItems.length > 0) {
      return data.lineItems
    }
    return []
  }, [data])

  const columns = useMemo<Array<Column<LineItem>>>(() => {
    const cols = [
      {
        Header: t('description', 'Description').toString(),
        accessor: values => values?.description ?? values.lineItemName,
        disableSortBy: true,
      },
      {
        Header: t('units_consumed', 'Units consumed').toString(),
        accessor: values => values.quantity,
      },
      {
        Header: t('cost_per_unit', 'Cost/Unit').toString(),
        accessor: ({ displayCurrency, unitCost, quantityPerUnit }) => {
          const symbol = currencySymbol(displayCurrency.toUpperCase())
          const formattedAmount = numeral(unitCost).format('0,0.00')

          return `${symbol} ${formattedAmount}/${quantityPerUnit} unit(s)`
        },
        disableSortBy: true,
      },
      {
        Header: t('amount', 'Amount').toString(),
        accessor: ({ displayCurrency, dueAmount }) => {
          const symbol = currencySymbol(displayCurrency.toUpperCase())
          const formattedAmount = numeral(dueAmount).format('0,0.00')

          return `${symbol} ${formattedAmount}`
        },
      },
    ]
    return cols
  }, [])

  const table = useTable<LineItem>(
    { columns, data: tableData ?? [] },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    table

  return loading ? (
    <Center height="100%">
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor="appPaleGrey"
        color="appBlue"
        size="xl"
      />
    </Center>
  ) : error ? (
    <Alert status="error" mb="1em">
      <AlertIcon />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : (
    <>
      <Table {...getTableProps} size="md">
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map(column => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps(
                    column.getSortByToggleProps(),
                  )
                  return (
                    <Th key={keyCol} {...restColumn}>
                      {column.render('Header')}
                      {!column.disableSortBy ? (
                        <Box as="span" pl="2" color={colors.appBattleshipGrey}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon
                                as={FaSortUp}
                                aria-label={t(
                                  'sorted_descending',
                                  'sorted descending',
                                )}
                              />
                            ) : (
                              <Icon
                                as={FaSortDown}
                                aria-label={t(
                                  'sorted_ascending',
                                  'sorted ascending',
                                )}
                              />
                            )
                          ) : (
                            <Icon
                              as={FaSort}
                              aria-label={t('sort_by_column', 'sort by column')}
                            />
                          )}
                        </Box>
                      ) : null}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.length ? (
            rows.map(row => {
              prepareRow(row)
              const { key, ...restRowProps } = row.getRowProps()
              return (
                <Tr key={key} {...restRowProps}>
                  {row.cells.map(cell => {
                    const { key: keyRow, ...restCellProps } =
                      cell.getCellProps()
                    return (
                      <Td key={keyRow} {...restCellProps}>
                        <Text>{cell.render('Cell')}</Text>
                      </Td>
                    )
                  })}
                </Tr>
              )
            })
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                <Text>
                  {t(
                    'upcoming_invoice_details_not_available',
                    'Upcoming invoice details are not available',
                  )}
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      <Link
        mt="1em"
        as={reactLink}
        lineHeight="normal"
        to="/invoices/"
        width="fit-content"
      >
        {t('go_back_to_invoices_page', 'Go back to the invoices page')}
      </Link>
    </>
  )
}

export default UpcomingInvoice
